import { useSearchParams } from "react-router-dom";
import ExperimentCard from "./ExperimentCard";
import sampleExperiment from "./sampleExperiment";

/**
 * /start — single entry point for sign-in AND sign-up.
 *
 * Left column: value props (mirrors the marketing landing page copy so the
 * pitch stays consistent across seosonar.me → app.seosonar.me).
 * Right column: the only auth action — Google OAuth with Search Console scope.
 */

const VALUE_PROPS = [
  {
    icon: "👀",
    title: "See 3 real points — before you pay",
    desc: "Connect Google and click \"Show me points for free\". We scan your actual site and hand you three specific opportunities. No payment until you decide it's worth it.",
  },
  {
    icon: "📡",
    title: "90 days of GSC data, analysed overnight",
    desc: "We pull every query, page, click and impression — then pattern-match what's actually underperforming.",
  },
  {
    icon: "⚡",
    title: "20+ scored experiments per report",
    desc: "Each one comes with the exact fix — title, meta, or new page spec — ranked by expected impact.",
  },
  {
    icon: "🔄",
    title: "Content refresh candidates",
    desc: "Pages that used to rank and are quietly slipping — we flag them and tell you exactly what to add, update, and cut so they bounce back.",
  },
  {
    icon: "🧠",
    title: "A Claude prompt for every fix",
    desc: "Paste it into your local repo and ship the change in minutes. No more 'now what do I do with this insight?'",
  },
  {
    icon: "⏱️",
    title: "Delivered in 24 hours",
    desc: "Two minutes of your time upfront. The rest happens while you sleep — back in your inbox by morning.",
  },
  {
    icon: "💸",
    title: "$49 one-time · no subscription",
    desc: "No dashboard to log into. No-questions-asked refund if it isn't useful.",
  },
];

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
      <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.26c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009.003 18z" fill="#34A853"/>
      <path d="M3.964 10.712A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.33z" fill="#FBBC05"/>
      <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0A8.997 8.997 0 00.957 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
    </svg>
  );
}

export default function StartPage() {
  const [params] = useSearchParams();
  const error = params.get("error");

  const handleGoogleSignIn = () => {
    window.location.href = "/google_login";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left — value props */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-semibold mb-5">
              📡 SeoSonar · SEO scan
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight mb-4">
              Your site is ranking.
              <br />
              You just don&apos;t know <em className="font-serif italic text-teal-600">why it&apos;s underperforming.</em>
            </h1>
            <p className="text-base text-slate-600 mb-8 max-w-lg">
              Connect your Google Search Console and let the sonar find it. One scan, one price — here&apos;s exactly what you get.
            </p>

            <ul className="space-y-4">
              {VALUE_PROPS.map((p) => (
                <li key={p.title} className="flex gap-3">
                  <span className="text-xl leading-none pt-0.5" aria-hidden="true">{p.icon}</span>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{p.title}</div>
                    <div className="text-sm text-slate-500">{p.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — Google sign-in */}
          <div className="lg:pl-8">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-10">
              <h2 className="text-xl font-extrabold text-slate-900 mb-2">
                Sign in to start your scan
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                New here or coming back — one click. We&apos;ll request read-only access to your Search Console. We can&apos;t make changes to your Google account.
              </p>

              <button
                onClick={handleGoogleSignIn}
                className="w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-lg border border-slate-200 bg-white text-slate-900 font-semibold hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <GoogleIcon />
                Continue with Google
              </button>

              {error && (
                <p className="mt-4 text-sm text-red-600">
                  Something went wrong signing in. Please try again.
                </p>
              )}

              <div className="mt-6 pt-6 border-t border-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Read-only Search Console access
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Encrypted token storage · revokable anytime
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  No subscription · no-questions-asked refund
                </div>
              </div>
            </div>

            <p className="text-xs text-slate-400 text-center mt-4">
              By continuing you agree to our{" "}
              <a
                href="https://www.seosonar.me/terms"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-slate-600"
              >
                Terms
              </a>{" "}
              and{" "}
              <a
                href="https://www.seosonar.me/privacy"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-slate-600"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>

        {/* What a report experiment looks like — live example rendered with
            the exact same card the paid report uses. */}
        <div className="mt-16 sm:mt-20">
          <div className="text-xs font-bold uppercase tracking-wide text-teal-700 mb-1">What you'll get</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2">
            What one report experiment looks like
          </h2>
          <p className="text-sm text-slate-600 max-w-2xl mb-6">
            Every fix ships with a score, the exact GSC signal, a step-by-step checklist, and a Claude prompt you can paste straight into Cursor. Your paid report contains 150+ of these tailored to your site.
          </p>
          <div className="max-w-2xl">
            <ExperimentCard exp={sampleExperiment} />
          </div>
        </div>
      </div>
    </div>
  );
}
